import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../../types';
import { useCallback, useMemo } from 'react';
import { HeatmapsClickAreaActions, HeatmapsClickAreaPayload } from '../actions';
import { isEmpty } from 'lodash';
import { AppCacheStatus } from '@fullstory/redux-util/utils';
import { Timestamp } from '@fullstory/proto/google/protobuf';

export type HeatmapClickAreaPayloadWithStorageId = {
  storageId: string;
  payload: HeatmapsClickAreaPayload | null;
};

export const useHeatmapClickAreaComputation = (
  payloadToFetch: HeatmapClickAreaPayloadWithStorageId,
) => {
  const heatmapZoneMetrics = useSelector((state: AppState) => state.heatmaps.heatmapZoneMetrics);
  const dispatch = useDispatch<AppDispatch>();
  const storageId = payloadToFetch.storageId;

  const forceCacheSwap = useCallback(() => {
    if (heatmapZoneMetrics[storageId]?.status === AppCacheStatus.HasData) {
      dispatch(HeatmapsClickAreaActions.appCacheSwap(storageId));
    }
  }, [storageId, dispatch, heatmapZoneMetrics]);

  const { namedElementStats, computedAt, status, timestamp } = useMemo(() => {
    if (isEmpty(heatmapZoneMetrics) || isEmpty(payloadToFetch.payload)) {
      return {};
    }

    const response = {
      data: heatmapZoneMetrics[storageId]?.data,
      status: heatmapZoneMetrics[storageId]?.status,
    };

    return {
      namedElementStats: response.data?.namedElementClickTypeStats,
      computedAt: response.data?.computedAt,
      status: response.status,
      timestamp: Timestamp.toDate(response?.data?.computedAt),
    };
  }, [payloadToFetch, storageId, heatmapZoneMetrics]);

  return {
    namedElementStats,
    computedAt,
    forceCacheSwap,
    status: status ?? AppCacheStatus.Initial,
    timestamp,
  };
};
